import { Stack, useTheme, Box, IconButton, Tooltip } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { defaultCenter } from '../../pages/ZonesPage/constants';
import { MapLatLng } from '../../pages/ZonesPage/interface';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { darkMapStyles } from '../../resources/constants';
import { useEffect, useState, useRef, useCallback } from 'react';
import { getContractСoordsHistoryAsync } from '../../redux/slices/contractsSlice';
import { ApiResponse } from './interface';

import trip_marker from './../../img/trip_marker.png';

interface Props {
  mapCenter?: MapLatLng;
  tripId?: string;
}

export const TripCoordinatesHistory = ({
  mapCenter = defaultCenter,
  tripId
}: Props) => {
  const theme = useTheme();
  const [path, setPath] = useState<MapLatLng[]>([]);
  const [startMarker, setStartMarker] = useState<MapLatLng | null>();
  const [endMarker, setEndMarker] = useState<MapLatLng | null>();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setIsMapLoaded(true);
  }, []);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom() || 11;
      mapRef.current.setZoom(currentZoom + 1);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom() || 11;
      mapRef.current.setZoom(currentZoom - 1);
    }
  }, []);

  const loadCoordinates = (id: string) => {
    getContractСoordsHistoryAsync(id).then((res: ApiResponse) => {
      console.log('-=-=- getContractСoordsHistoryAsync', res);
      if (res.contract.coordsHistory) {
        const polyLinePath: MapLatLng[] = res.contract.coordsHistory
          .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
          .map(coord => ({
            lat: coord.lat,
            lng: coord.lon
          }));
        setPath(polyLinePath);
      }

      if (res.contract.startCoords?.lat && res.contract.startCoords?.lon) {
        setStartMarker({
          lat: res.contract.startCoords?.lat,
          lng: res.contract.startCoords?.lon
        });
      }

      if (res.contract.exitCoords?.lat && res.contract.exitCoords?.lon) {
        setEndMarker({
          lat: res.contract.exitCoords?.lat,
          lng: res.contract.exitCoords?.lon
        });
      }
    });
  };

  useEffect(() => {
    if (tripId) {
      loadCoordinates(tripId);
    }
  }, [tripId]);

  useEffect(() => {
    if (isMapLoaded && mapRef.current && (startMarker || endMarker)) {
      const bounds = new google.maps.LatLngBounds();
      
      if (startMarker) {
        bounds.extend(new google.maps.LatLng(startMarker.lat, startMarker.lng));
      }
      
      if (endMarker) {
        bounds.extend(new google.maps.LatLng(endMarker.lat, endMarker.lng));
      }
      
      mapRef.current.fitBounds(bounds);
      
      const listener = google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
        const currentZoom = mapRef.current?.getZoom() || 11;
        if (mapRef.current) {
          mapRef.current.setZoom(currentZoom - 1);
        }
      });
      
      return () => {
        google.maps.event.removeListener(listener);
      };
    }
  }, [isMapLoaded, startMarker, endMarker]);

  return (
    <Stack direction='row' alignItems='center' spacing={2}>
      <Stack width={'100%'} sx={{ position: 'relative' }}>
        {/* Zoom Controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 30,
            right: 10,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Tooltip title="Збільшити" placement="left">
            <IconButton
              onClick={handleZoomIn}
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
                color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242'
                }
              }}
            >
              <Add />
            </IconButton>
          </Tooltip>
          <Tooltip title="Зменшити" placement="left">
            <IconButton
              onClick={handleZoomOut}
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
                color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242'
                }
              }}
            >
              <Remove />
            </IconButton>
          </Tooltip>
        </Box>

        <GoogleMap
          center={mapCenter}
          mapContainerStyle={{
            width: '100%',
            height: 500,
            marginTop: 20
          }}
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            fullscreenControl: true,
            styles: theme.palette.mode === 'dark' ? darkMapStyles : undefined
          }}
          zoom={11}
          onLoad={onLoad}
        >
          {startMarker ? (
            <Marker
              position={startMarker}
              icon={{
                url: trip_marker,
                labelOrigin: new window.google.maps.Point(16, 15)
              }}
              label={{
                text: 'A',
                fontSize: '13px'
              }}
              zIndex={10}
            />
          ) : null}

          {endMarker ? (
            <Marker
              position={endMarker}
              icon={{
                url: trip_marker,
                labelOrigin: new window.google.maps.Point(16, 15)
              }}
              label={{
                text: 'B',
                fontSize: '13px'
              }}
            />
          ) : null}

          <Polyline
            path={path}
            visible
            options={{
              strokeWeight: 1,
              strokeColor: 'red'
            }}
          />
        </GoogleMap>
      </Stack>
    </Stack>
  );
};
